import { useCallback } from "react";
import { getCTAUrl } from "../../../utils/getCTA";
import { useNavigate } from "react-router";
import { Comic, Release } from "../../../types";
import {
  useGetReaderNavigationContext,
  UseGetReaderNavigationContextProps,
} from "./useGetReaderNavigationContext";
import { readerNavigationContext } from "../../../hookstate/readerNavigationContextStore";
import { ReaderNavigationContext } from "../types";

export function useNavigateToReaderWithContext(
  props: UseGetReaderNavigationContextProps & {
    releaseToNavigate?: Release;
    comicToNavigate?: Comic;
    pageToNavigate?: number;
  } & Partial<ReaderNavigationContext>,
  dependencies: ReadonlyArray<unknown> = [],
) {
  const {
    comicToNavigate,
    releaseToNavigate,
    trigger,
    contextComicId,
    section,
    cardPosition,
    sectionPosition,
    notificationTypeId,
    area,
    pageToNavigate,
    // Remaining props are extra ReaderNavigationContext fields (q, searchQueryId, etc.)
    ...extraContext
  } = props;

  const navigationContext = useGetReaderNavigationContext({
    trigger,
    contextComicId,
    section,
    cardPosition,
    sectionPosition,
    notificationTypeId,
    area,
  });

  const navigate = useNavigate();

  const navigateToCta = useCallback(
    (overrides?: Partial<ReaderNavigationContext>) => {
      readerNavigationContext.set({
        ...navigationContext,
        ...extraContext,
        ...(overrides ?? {}),
      });
      const entityToNavigate = releaseToNavigate ?? comicToNavigate;
      if (entityToNavigate) {
        let ctaUrl = getCTAUrl(entityToNavigate);
        if (pageToNavigate) {
          ctaUrl = ctaUrl.replace(/\/\d+$/, `/${pageToNavigate}`);
        }

        navigate(ctaUrl);
      }
    },
    [
      navigationContext,
      releaseToNavigate,
      comicToNavigate,
      pageToNavigate,
      navigate,
    ],
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  const goToCTAUrlWithContext = useCallback(
    (overrides?: Partial<ReaderNavigationContext>) => {
      navigateToCta(overrides);
    },
    [navigateToCta, ...dependencies],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  const goToCTAUrl = useCallback(() => {
    navigateToCta();
  }, [navigateToCta]);

  return {
    goToCTAUrl,
    goToCTAUrlWithContext,
    navigationContext,
  };
}
